import { useState, useEffect, useRef, useCallback } from 'react';
import type { PostgrestError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { retrySupabaseOperation } from '@/lib/error-handling';
import { useAuth } from './useAuth';

export interface NavigationContext {
  activeRFQs: number;
  newQuotes: number;
  unreadMessages: number;
  pendingOrders: number;
  activeRFQsMarketplace: number;
  pendingQuotes: number;
  activeRFQCount: number;
  activeOrderCount: number;
  pendingOrderCount: number;
  pendingQuoteCount: number;
  pendingSampleCount: number;
  unreadMessageCount: number;
  loading: boolean;
}

const isTransient503 = (error: Partial<PostgrestError> & { status?: number } = {}) => {
  return (
    error.code === '503' ||
    error.status === 503 ||
    (typeof error.message === 'string' && error.message.includes('503'))
  );
};

export function useNavigationContext() {
  const { user } = useAuth();
  const [context, setContext] = useState<NavigationContext>({
    activeRFQs: 0,
    newQuotes: 0,
    unreadMessages: 0,
    pendingOrders: 0,
    activeRFQsMarketplace: 0,
    pendingQuotes: 0,
    activeRFQCount: 0,
    activeOrderCount: 0,
    pendingOrderCount: 0,
    pendingQuoteCount: 0,
    pendingSampleCount: 0,
    unreadMessageCount: 0,
    loading: true,
  });

  // Simplified refs to prevent resource exhaustion
  const mountedRef = useRef(true);
  const lastUpdateRef = useRef(0);
  const isLoadingRef = useRef(false);
  const consecutiveErrorsRef = useRef(0);
  const MIN_UPDATE_INTERVAL = 5000; // 5 seconds minimum between updates

  const loadNavigationContext = useCallback(async () => {
    if (!user || !mountedRef.current || isLoadingRef.current) return;

    isLoadingRef.current = true;
    lastUpdateRef.current = Date.now();

    // Simple error handling with retry logic for 503 errors
    const safeCount = async (
      queryFn: () => Promise<{ count: number | null; error: PostgrestError | null }>
    ) => {
      try {
        const result = await retrySupabaseOperation(queryFn, 2, 1000);
        if (result.error) {
          // Only log non-503 errors to avoid console spam
          const error = result.error;
          if (!isTransient503(error)) {
            logger.warn('Navigation context query error', { error });
          }
          return 0;
        }
        return result.count || 0;
      } catch (error) {
        // Only log non-503 errors
        const err = (error as Partial<PostgrestError> & { status?: number }) ?? {};
        if (!isTransient503(err)) {
          logger.warn('Navigation context query exception', { error });
        }
        return 0;
      }
    };

    // Helper function to safely fetch data with error handling and retry logic
    async function safeData<T>(
      queryFn: () => Promise<{ data: T | null; error: PostgrestError | null }>
    ): Promise<T | null> {
      try {
        const result = await retrySupabaseOperation(queryFn, 2, 1000);
        if (result.error) {
          // Only log non-503 errors to avoid console spam
          const error = result.error;
          if (!isTransient503(error)) {
            logger.warn('Navigation context query error', { error });
          }
          return null;
        }
        return result.data;
      } catch (error) {
        // Only log non-503 errors
        const err = (error as Partial<PostgrestError> & { status?: number }) ?? {};
        if (!isTransient503(err)) {
          logger.warn('Navigation context query exception', { error });
        }
        return null;
      }
    }

    try {
      // Batch all queries to reduce API calls and use Promise.allSettled for efficiency
      const [
        rfqsCount,
        marketplaceRfqsCount,
        pendingQuotesCount,
        ordersCount,
        activeOrdersCount,
        pendingSamplesCount,
        conversations
      ] = await Promise.allSettled([
        // Active RFQs (buyer)
        safeCount(
          () => supabase
            .from('rfqs')
            .select('id', { count: 'exact', head: true })
            .eq('buyer_id', user.id)
            .in('status', ['pending', 'quoted'])
        ),
        // Marketplace RFQs (supplier) - Issue #4 fix: Use same statuses as dashboard
        safeCount(
          () => supabase
            .from('rfqs')
            .select('id', { count: 'exact', head: true })
            .in('status', ['pending', 'active', 'open'])
        ),
        // Pending quotes (supplier)
        safeCount(
          () => supabase
            .from('quotes')
            .select('id', { count: 'exact', head: true })
            .eq('supplier_id', user.id)
            .eq('status', 'pending')
        ),
        // Pending orders
        safeCount(
          () => supabase
            .from('orders')
            .select('id', { count: 'exact', head: true })
            .or(`buyer_id.eq.${user.id},supplier_id.eq.${user.id}`)
            .in('status', ['pending', 'processing'])
        ),
        // Active orders (buyer)
        safeCount(
          () => supabase
            .from('orders')
            .select('id', { count: 'exact', head: true })
            .eq('buyer_id', user.id)
            .in('status', ['processing', 'shipped', 'in_transit'])
        ),
        // Pending samples (buyer)
        safeCount(
          () => supabase
            .from('sample_requests')
            .select('id', { count: 'exact', head: true })
            .eq('buyer_id', user.id)
            .in('status', ['pending', 'approved', 'awaiting_payment'])
        ),
        // Conversations for unread messages
        safeData(
          () => supabase
            .from('conversations')
            .select('id')
            .or(`buyer_id.eq.${user.id},supplier_id.eq.${user.id}`)
        )
      ]);

      // Extract values from settled promises
      const rfqsCountValue = rfqsCount.status === 'fulfilled' ? rfqsCount.value : 0;
      const marketplaceRfqsCountValue = marketplaceRfqsCount.status === 'fulfilled' ? marketplaceRfqsCount.value : 0;
      const pendingQuotesCountValue = pendingQuotesCount.status === 'fulfilled' ? pendingQuotesCount.value : 0;
      const ordersCountValue = ordersCount.status === 'fulfilled' ? ordersCount.value : 0;
      const activeOrdersCountValue = activeOrdersCount.status === 'fulfilled' ? activeOrdersCount.value : 0;
      const pendingSamplesCountValue = pendingSamplesCount.status === 'fulfilled' ? pendingSamplesCount.value : 0;
      const conversationsData = conversations.status === 'fulfilled' ? conversations.value : null;

      // Calculate unread messages (this is more complex, so do it separately)
      let unreadCount = 0;
      if (conversationsData && conversationsData.length > 0) {
        // Limit to first 10 conversations to prevent too many queries
        const limitedConversations = conversationsData.slice(0, 10);
        const unreadPromises = limitedConversations.map(conv =>
          safeCount(
            () => supabase
              .from('messages')
              .select('id', { count: 'exact', head: true })
              .eq('conversation_id', conv.id)
              .neq('sender_id', user.id)
              .is('read_at', null)
          )
        );

        const unreadResults = await Promise.allSettled(unreadPromises);
        unreadCount = unreadResults.reduce((total, result) => {
          return total + (result.status === 'fulfilled' ? result.value : 0);
        }, 0);
      }

      // Get recent quotes for buyer (simplified - just count all pending quotes for buyer's RFQs)
      let newQuotesCount = 0;
      let buyerPendingQuotesCount = 0;
      try {
        const buyerRfqs = await safeData(
          () => supabase
            .from('rfqs')
            .select('id')
            .eq('buyer_id', user.id)
            .limit(50) // Limit to prevent excessive queries
        );

        if (buyerRfqs && buyerRfqs.length > 0) {
          const rfqIds = buyerRfqs.map(r => r.id);

          // Get quotes from last 24 hours
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);

          const [newQuotes, quotes] = await Promise.allSettled([
            safeCount(
              () => supabase
                .from('quotes')
                .select('id', { count: 'exact', head: true })
                .in('rfq_id', rfqIds)
                .gte('created_at', yesterday.toISOString())
                .eq('status', 'pending')
            ),
            safeCount(
              () => supabase
                .from('quotes')
                .select('id', { count: 'exact', head: true })
                .in('rfq_id', rfqIds)
                .eq('status', 'pending')
            )
          ]);

          newQuotesCount = newQuotes.status === 'fulfilled' ? newQuotes.value : 0;
          buyerPendingQuotesCount = quotes.status === 'fulfilled' ? quotes.value : 0;
        }
      } catch (error) {
        logger.warn('Error fetching buyer quotes data', { error });
      }

      if (mountedRef.current) {
        setContext({
          activeRFQs: rfqsCountValue,
          newQuotes: newQuotesCount,
          unreadMessages: unreadCount,
          pendingOrders: ordersCountValue,
          activeRFQsMarketplace: marketplaceRfqsCountValue,
          pendingQuotes: pendingQuotesCountValue,
          activeRFQCount: rfqsCountValue,
          activeOrderCount: activeOrdersCountValue,
          pendingOrderCount: ordersCountValue,
          pendingQuoteCount: buyerPendingQuotesCount,
          pendingSampleCount: pendingSamplesCountValue,
          unreadMessageCount: unreadCount,
          loading: false,
        });
      }
    } catch (error) {
      logger.error('Error loading navigation context', { error });
      if (mountedRef.current) {
        setContext(prev => ({ ...prev, loading: false }));
      }
    } finally {
      isLoadingRef.current = false;
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setContext(prev => ({ ...prev, loading: false }));
      return;
    }

    mountedRef.current = true;

    // Initial load
    loadNavigationContext();

    // Set up a single periodic refresh instead of realtime subscriptions
    // This prevents excessive API calls while still keeping counts somewhat fresh
    const intervalId = setInterval(() => {
      if (mountedRef.current && !isLoadingRef.current) {
        const now = Date.now();
        if (now - lastUpdateRef.current >= MIN_UPDATE_INTERVAL) {
          loadNavigationContext();
        }
      }
    }, 30000); // Check every 30 seconds

    return () => {
      mountedRef.current = false;
      clearInterval(intervalId);
    };
  }, [user, loadNavigationContext]);

  return context;
}
